import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowRight, CheckCircle2, Sparkles, Globe, Search, FileSearch, Users, BarChart3 } from "lucide-react";

const T = {
  cream:    "#F7F4EE",
  ink:      "#0A192F",
  inkFaint: "#5A6478",
  line:     "#E5E1D8",
  lineSoft: "rgba(10,25,47,0.16)",
  coral:    "#E24E1B",
  coralDark:"#B33E15",
  brass:    "#C8A464",
  sage:     "#4F7A5B",
};
const FONT_DISPLAY = "'Fraunces', 'Playfair Display', Georgia, serif";
const FONT_MONO    = "'JetBrains Mono', 'IBM Plex Mono', monospace";

/**
 * /free-ai-seo-audit — Newstandard-style free audit landing + intake form.
 * Submits to POST /api/public/ai-seo-audit then redirects to /ai-seo-audit-report/:id.
 */
export default function PublicAiSeoAudit() {
  const nav = useNavigate();
  const [f, setF] = useState({ business_name: "", website: "", industry: "", services: "", email: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name.trim() || !f.website.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) {
      return toast.error("Business, website and a valid email are required.");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/ai-seo-audit", {
        business_name: f.business_name.trim(),
        website:       f.website.trim(),
        industry:      f.industry.trim(),
        services:      f.services.trim(),
        email:         f.email.trim(),
      });
      toast.success("Audit started — redirecting…");
      nav(data.report_url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="public-ai-seo-audit-page" style={{ minHeight: "100vh", background: T.cream, color: T.ink, fontFamily: "Inter, -apple-system, sans-serif" }}>
      <Toaster richColors position="top-right" />

      {/* NAV */}
      <nav style={{ padding: "20px 28px", borderBottom: `1px solid ${T.lineSoft}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 700, color: T.ink, textDecoration: "none" }}>
            Clients OnRamp
          </Link>
          <Link to="/" style={{ fontSize: 13, color: T.inkFaint, textDecoration: "none" }}>← back to site</Link>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ padding: "80px 28px 60px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 56, alignItems: "start" }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.16em", textTransform: "uppercase", color: T.coral, fontWeight: 600 }}>
              Free AI SEO visibility audit
            </div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: 60, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1.03, margin: "16px 0 24px" }}>
              See how <em style={{ fontStyle: "italic", color: T.coral }}>ChatGPT</em>, <em style={{ fontStyle: "italic", color: T.coral }}>Gemini</em>, <em style={{ fontStyle: "italic", color: T.coral }}>Claude</em> and <em style={{ fontStyle: "italic", color: T.coral }}>Perplexity</em> rank your brand.
            </h1>
            <p style={{ fontSize: 18, color: T.inkFaint, lineHeight: 1.55, maxWidth: 560, margin: "0 0 32px" }}>
              We run 10 real buyer-intent queries against all four AI engines, then show you exactly which brands they recommend — and which pages they cite. Free. No credit card. Report in ~2 minutes.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, margin: "0 0 36px", maxWidth: 560 }}>
              {[
                { icon: Globe,     stat: "4 engines",     label: "ChatGPT, Gemini, Claude, Perplexity" },
                { icon: Search,    stat: "10 prompts",    label: "Auto-generated buyer-intent queries" },
                { icon: BarChart3, stat: "Share of voice", label: "Which competitors dominate your space" },
                { icon: FileSearch,stat: "Full citations", label: "Every page AI cited — not just yours" },
              ].map((s, i) => {
                const Ic = s.icon;
                return (
                  <div key={i} style={{ padding: "16px 18px", background: "rgba(255,255,255,0.6)", border: `1px solid ${T.lineSoft}` }}>
                    <Ic size={18} color={T.coral} />
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, marginTop: 8, letterSpacing: "-0.02em" }}>{s.stat}</div>
                    <div style={{ fontSize: 12, color: T.inkFaint, marginTop: 2, lineHeight: 1.4 }}>{s.label}</div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 16, fontSize: 13, color: T.inkFaint }}>
              <span><CheckCircle2 size={14} color={T.sage} style={{ display: "inline", marginRight: 4, marginBottom: -2 }} />No signup required</span>
              <span><CheckCircle2 size={14} color={T.sage} style={{ display: "inline", marginRight: 4, marginBottom: -2 }} />Shareable public URL</span>
              <span><CheckCircle2 size={14} color={T.sage} style={{ display: "inline", marginRight: 4, marginBottom: -2 }} />30-day re-audit free</span>
            </div>
          </div>

          {/* FORM */}
          <div data-testid="audit-form-card" style={{ background: "#fff", padding: "36px 32px", border: `1px solid ${T.lineSoft}` }}>
            <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.coralDark, fontWeight: 700 }}>
              Request your free audit
            </div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 500, lineHeight: 1.15, margin: "10px 0 4px" }}>
              Takes <em style={{ fontStyle: "italic", color: T.coral }}>60 seconds</em>.
            </h2>
            <p style={{ fontSize: 13, color: T.inkFaint, margin: "0 0 22px", lineHeight: 1.55 }}>
              We'll email you the shareable report when it's ready (usually under 2 minutes).
            </p>
            <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
              <input required placeholder="Business name" value={f.business_name}
                     onChange={(e) => setF({ ...f, business_name: e.target.value })}
                     data-testid="audit-business-name"
                     style={{ padding: "13px 16px", fontSize: 14, border: `1px solid ${T.line}`, outline: "none", background: "#fff", color: T.ink }} />
              <input required placeholder="https://yourbusiness.com" value={f.website}
                     onChange={(e) => setF({ ...f, website: e.target.value })}
                     data-testid="audit-website"
                     style={{ padding: "13px 16px", fontSize: 14, border: `1px solid ${T.line}`, outline: "none", background: "#fff", color: T.ink }} />
              <input placeholder="Industry (e.g. plumbing, dental, SaaS)" value={f.industry}
                     onChange={(e) => setF({ ...f, industry: e.target.value })}
                     data-testid="audit-industry"
                     style={{ padding: "13px 16px", fontSize: 14, border: `1px solid ${T.line}`, outline: "none", background: "#fff", color: T.ink }} />
              <input placeholder="Services offered (optional)" value={f.services}
                     onChange={(e) => setF({ ...f, services: e.target.value })}
                     data-testid="audit-services"
                     style={{ padding: "13px 16px", fontSize: 14, border: `1px solid ${T.line}`, outline: "none", background: "#fff", color: T.ink }} />
              <input required type="email" placeholder="you@yourbusiness.com" value={f.email}
                     onChange={(e) => setF({ ...f, email: e.target.value })}
                     data-testid="audit-email"
                     style={{ padding: "13px 16px", fontSize: 14, border: `1px solid ${T.line}`, outline: "none", background: "#fff", color: T.ink }} />
              <button type="submit" disabled={busy} data-testid="audit-submit"
                      style={{ background: T.coral, color: T.cream, border: "none", padding: "15px 24px", fontSize: 15, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                {busy ? "Starting audit…" : "Run my free AI SEO audit"} <ArrowRight size={16} />
              </button>
              <p style={{ fontSize: 11, color: T.inkFaint, textAlign: "center", margin: "6px 0 0", lineHeight: 1.4 }}>
                No signup · Report emailed in ~2 min · Free re-audit after 30 days
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding: "60px 28px 100px", borderTop: `1px solid ${T.lineSoft}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.16em", textTransform: "uppercase", color: T.brass, fontWeight: 600, marginBottom: 10 }}>
            How it works
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: 400, letterSpacing: "-0.02em", margin: "0 0 56px", maxWidth: 720 }}>
            Four engines. Ten prompts. One honest report.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 28 }}>
            {[
              { n: "01", title: "We auto-generate 10 prompts",   body: "Buyer-intent queries matching your industry — generic, niche, problem-first, and competitor-comparison blends." },
              { n: "02", title: "We query all 4 engines",         body: "ChatGPT (via web search), Gemini (googleSearch), Claude Sonnet 5.5 (web_search), and Perplexity Sonar." },
              { n: "03", title: "We score + rank + compare",      body: "Visibility score, share-of-voice vs competitors, citation stats, and the top pages AI references." },
              { n: "04", title: "You get a public report URL",    body: "Shareable with your team or clients. 30-day re-audit free so you can track movement." },
            ].map((s) => (
              <div key={s.n}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.coral, fontWeight: 700, letterSpacing: "0.1em" }}>{s.n}</div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, marginTop: 8, letterSpacing: "-0.01em" }}>{s.title}</div>
                <p style={{ fontSize: 14, color: T.inkFaint, lineHeight: 1.55, marginTop: 10 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer style={{ padding: "32px 28px", borderTop: `1px solid ${T.lineSoft}`, fontSize: 12, color: T.inkFaint, textAlign: "center" }}>
        <Link to="/" style={{ color: T.inkFaint, textDecoration: "none", borderBottom: `1px solid ${T.inkFaint}` }}>Clients OnRamp</Link>
        <span> · all-in-one lead gen + delivery for local businesses</span>
      </footer>
    </div>
  );
}
